<template>
    <div class="mb-6">
        <page-headline>
            {{$t('pages.profile.title')}}
        </page-headline>
        <div class="flex mt-3">
            <div class="w-1/5">
                <div class="label">{{$t('pages.profile.username')}}</div>
                <div class="label mt-3">{{$t('pages.profile.email')}}</div>
                <div class="label mt-3">{{$t('pages.profile.tenant')}}</div>
            </div>
            <div class="w-4/5">
                <div class="label">{{fullName}}</div>
                <div class="label mt-3">{{email}}</div>
                <div class="label mt-3">{{tenant}}</div>
            </div>
        </div>
        <div class="pt-10">{{$t('pages.profile.change')}}</div>
        <div class="flex mt-8">
            <div class="w-1/5">{{$t('pages.profile.password')}}<span class="pl-3">*******</span></div>
            <div class="w-4/5">
                <button type="button" class="btn btn-secondary ml-5" @click="changePassword">{{$t('pages.profile.buttons.change_password')}}</button>
            </div>
        </div>
        <div class="mt-10" v-if="passwordChanged">
            {{$t('pages.profile.password_changed')}}
        </div>
        <alert-messages
            v-if="passwordChanged"
            :error-type="ERROR_TYPE_INFO"
            :messages="[$t('pages.profile.password_changed')]"
            :display-errors-title="false"
            class="mt-10"
        />
        <modal ref="changePasswordDialog"
               @confirm="onSubmitPasswordChange"
               @cancel="onCancelPasswordChange"
               @show="onShowPasswordChange"
               :title="this.$t('pages.profile.modal.title')">
            <form ref="changePasswordForm">
                <div class="flex w-full content-between relative">
                    <label for="currentPassword" class="w-1/2 ">{{$t('pages.profile.modal.form.current_password')}}</label>
                    <input ref="currentPassword"
                           type="password"
                           @keyup="validateOldPassword"
                           name="currentPassword"
                           id="currentPassword"
                           class="w-1/2" :class="{'error' : !isOldPasswordValid}" :placeholder="'********'">
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <icon name="exclamation" class="h-5 w-5 text-red-500" v-if="!isOldPasswordValid"></icon>
                    </div>
                </div>
                <div class="flex w-full content-between relative mt-5">
                    <label for="newPassword" class="w-1/2 ">{{$t('pages.profile.modal.form.new_password')}}</label>
                    <input ref="newPassword"
                           type="password"
                           @keyup="validateNewPassword"
                           name="newPassword"
                           id="newPassword"
                           class="w-1/2" :class="{'error' : !isNewPasswordValid}" :placeholder="'********'">
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <icon name="exclamation" class="h-5 w-5 text-red-500" v-if="!isNewPasswordValid"></icon>
                    </div>
                </div>
                <div class="flex w-full content-between relative mt-5">
                    <label for="confirmPassword" class="w-1/2 ">{{$t('pages.profile.modal.form.confirm_password')}}</label>
                    <input ref="confirmPassword"
                           type="password"
                           @keyup="validateConfirmPassword"
                           name="confirmPassword"
                           id="confirmPassword"
                           class="w-1/2" :class="{'error' : !isConfirmPasswordValid}" :placeholder="'********'">
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <icon name="exclamation" class="h-5 w-5 text-red-500" v-if="!isConfirmPasswordValid"></icon>
                    </div>
                </div>
            </form>
            <alert-messages
                v-if="this.errors && this.errors.length > 0"
                :error-type="ERROR_TYPE_ERROR"
                :messages="this.errors"
                :display-errors-title="false"
                :headline="$t('pages.profile.password_changed_errors')"
                class="mt-10"
            />
        </modal>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import PageHeadline from "@/Shared/PageHeadline";
import Icon from "@/Shared/Icon";
import Modal from "@/Shared/Modals/Modal";
import AlertMessages from "../../Shared/Alerts/AlertMessage";
import {ErrorTypes} from "../../mixins/ErrorTypes";

export default {
    metaInfo: {title: 'Profile'},
    layout: Layout,
    components: {
        AlertMessages,
        PageHeadline,
        Icon,
        Modal
    },
    mixins:[
        ErrorTypes
    ],
    data() {
        return {
            fullName: this.$page.props.auth.user.first_name + ', ' + this.$page.props.auth.user.last_name,
            email: this.$page.props.auth.user.email,
            tenant: this.$page.props.auth.user.account?.name ?? '-',
            sending: false,
            passwordChanged: false,
            errors: [],
            isConfirmPasswordValid: true,
            isNewPasswordValid : true,
            isOldPasswordValid : true
        }
    },
    methods: {
        changePassword() {
            let dlg = this.$refs.changePasswordDialog;
            if(dlg) {
                dlg.show();
                this.$nextTick(() => {
                    let oldPass = this.$refs.currentPassword;
                    if(oldPass) {
                        oldPass.focus();
                    }
                });
            }
        },
        onSubmitPasswordChange() {
            let dlg = this.$refs.changePasswordDialog;
            if(dlg) {
                if(!this.checkForm()) { // First check the form
                    return;
                }

                axios.post(route('changePassword'), {
                    current_password: this.$refs.currentPassword.value,
                    new_password: this.$refs.newPassword.value,
                    confirm_password: this.$refs.confirmPassword.value,
                })
                .then(() => {
                    this.sending = false;
                    this.passwordChanged = true;
                    dlg.hide();
                })
                .catch((err) => {
                    this.sending = false;
                    if(err.response && err.response.data && err.response.data.errors) {
                        this.errors = err.response.data.errors;
                    }
                });
            }
        },
        onCancelPasswordChange() {
            let dlg = this.$refs.changePasswordDialog;
            if(dlg) {
                dlg.hide();
                this.isConfirmPasswordValid = true;
                this.isNewPasswordValid = true;
                this.isOldPasswordValid = true;
                this.errors = [];
            }
        },
        onShowPasswordChange() {
          this.passwordChanged = false;
          this.errors = [];
        },
        validateOldPassword() {
            let pass = this.$refs.currentPassword;
            this.isOldPasswordValid = (pass && pass.value.length > 0);
        },
        validateNewPassword() {
            let pass = this.$refs.newPassword;
            let confirm = this.$refs.confirmPassword;
            this.isNewPasswordValid = (pass && confirm && pass.value.length > 0 && confirm.value === pass.value);
            this.isConfirmPasswordValid = (pass && confirm && confirm.value.length > 0 && confirm.value === pass.value);
        },
        validateConfirmPassword() {
            let pass = this.$refs.newPassword;
            let confirm = this.$refs.confirmPassword;
            this.isNewPasswordValid = (pass && confirm && pass.value.length > 0 && confirm.value === pass.value);
            this.isConfirmPasswordValid = (pass && confirm && confirm.value.length > 0 && confirm.value === pass.value);
        },
        checkForm() {
            this.errors = [];
            let retVal = true;
            this.validateOldPassword();
            this.validateNewPassword();
            this.validateConfirmPassword();

            if(!this.isOldPasswordValid) {
                retVal = false;
                this.errors.push(this.$t('pages.profile.errors.old_password_required'));
            }

            let pass = this.$refs.newPassword;
            let confirm = this.$refs.confirmPassword;
            if(pass && confirm) {
                if(pass.value.length < 1) {
                    retVal = false;
                    this.errors.push(this.$t('pages.profile.errors.new_password_required'));
                }
                if(confirm.value.length < 1) {
                    retVal = false;
                    this.errors.push(this.$t('pages.profile.errors.confirm_password_required'));
                }
                if(confirm.value !== pass.value) {
                    retVal = false;
                    this.errors.push(this.$t('pages.profile.errors.passwords_do_not_match'));
                }
            }
            return retVal;
        }
    }
}
</script>
